import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateCalendarEventBody,
  CreateDayOffBody,
  ListCalendarEventsQuery,
  ListDayOffsQuery,
  ListLinkedItemsQuery,
  UpdateCalendarEventBody,
} from "@/modules/management/calendar/domain/types";
import { managementCalendarApi } from "./client";

const calendarKeys = {
  all: ["management", "calendar"] as const,
  events: (q: ListCalendarEventsQuery) =>
    [...calendarKeys.all, "events", q] as const,
  event: (id: string) => [...calendarKeys.all, "event", id] as const,
  linkedItems: (q: ListLinkedItemsQuery) =>
    [...calendarKeys.all, "linkedItems", q] as const,
  dayOffs: (q: ListDayOffsQuery) =>
    [...calendarKeys.all, "dayOffs", q] as const,
};

export function useCalendarEvents(query: ListCalendarEventsQuery) {
  return useQuery({
    queryKey: calendarKeys.events(query),
    queryFn: () => managementCalendarApi.listEvents(query),
  });
}

export function useCalendarEvent(id: string | null) {
  return useQuery({
    queryKey: calendarKeys.event(id ?? ""),
    queryFn: () => managementCalendarApi.getEventById(id!),
    enabled: !!id,
  });
}

export function useCreateCalendarEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCalendarEventBody) =>
      managementCalendarApi.createEvent(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calendarKeys.all });
    },
  });
}

export function useUpdateCalendarEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: { id: string; body: UpdateCalendarEventBody }) =>
      managementCalendarApi.updateEvent(id, body),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: calendarKeys.all });
      qc.invalidateQueries({ queryKey: calendarKeys.event(id) });
    },
  });
}

export function useDeleteCalendarEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => managementCalendarApi.deleteEvent(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calendarKeys.all });
    },
  });
}

export function useCalendarLinkedItems(query: ListLinkedItemsQuery | null) {
  return useQuery({
    queryKey: query
      ? calendarKeys.linkedItems(query)
      : ([...calendarKeys.all, "linkedItems", "skip"] as const),
    queryFn: () => managementCalendarApi.listLinkedItems(query!),
    enabled: !!query?.userId && !!query?.start && !!query?.end,
  });
}

export function useWorkCalendarDayOffs(query: ListDayOffsQuery) {
  return useQuery({
    queryKey: calendarKeys.dayOffs(query),
    queryFn: () => managementCalendarApi.listDayOffs(query),
  });
}

export function useCreateDayOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDayOffBody) =>
      managementCalendarApi.createDayOff(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calendarKeys.all });
    },
  });
}

export function useDeleteDayOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => managementCalendarApi.deleteDayOff(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: calendarKeys.all });
    },
  });
}
